import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Paperclip } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

type Address = { emailAddress?: { name?: string; address?: string } };

type EmailView = {
  id: string;
  subject: string | null;
  from: Address | null;
  to: Address[];
  cc: Address[];
  date: string;
  hasAttachments: boolean;
  mailboxOwner: string | null;
  body: { contentType: "html" | "text"; content: string } | null;
  preview: string | null;
  unavailable: string | null;
};

const fmt = (a: Address | null | undefined) => {
  const name = a?.emailAddress?.name;
  const addr = a?.emailAddress?.address;
  return name && addr && name !== addr ? `${name} <${addr}>` : addr || name || "";
};
const fmtList = (xs: Address[] | null | undefined) => (xs ?? []).map(fmt).filter(Boolean).join(", ");

/**
 * Reads a synced email inside the CRM. Used for copies in a teammate's mailbox,
 * which Outlook cannot open for the viewer.
 *
 * The body is someone else's HTML, so it renders in a sandboxed iframe: no
 * scripts, no same-origin access, and links open in a new tab.
 */
export function EmailViewerDialog({ id, open, onOpenChange }: { id: string; open: boolean; onOpenChange: (o: boolean) => void }) {
  const q = useQuery<EmailView>({ queryKey: ["/api/emails", id, "full"], enabled: open, staleTime: 5 * 60_000 });
  const v = q.data;

  const srcDoc = useMemo(() => {
    if (!v?.body || v.body.contentType !== "html") return null;
    return `<!doctype html><html><head><meta charset="utf-8"><base target="_blank">
      <style>body{margin:0;padding:12px;background:#fff;color:#111;font:14px/1.5 -apple-system,"Segoe UI",sans-serif;overflow-wrap:anywhere}img{max-width:100%;height:auto}</style>
      </head><body>${v.body.content}</body></html>`;
  }, [v]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="pr-6">{v?.subject || "(no subject)"}</DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-0.5 text-xs">
              {v && (
                <>
                  <div><span className="text-muted-foreground">From </span>{fmt(v.from)}</div>
                  {v.to.length > 0 && <div><span className="text-muted-foreground">To </span>{fmtList(v.to)}</div>}
                  {v.cc.length > 0 && <div><span className="text-muted-foreground">Cc </span>{fmtList(v.cc)}</div>}
                  <div className="flex items-center gap-2">
                    <span>{new Date(v.date).toLocaleString()}</span>
                    {v.mailboxOwner && <span className="text-muted-foreground">· in {v.mailboxOwner}'s mailbox</span>}
                    {v.hasAttachments && <span className="flex items-center gap-0.5 text-muted-foreground"><Paperclip className="w-3 h-3" />Attachments</span>}
                  </div>
                </>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>

        {q.isLoading && (
          <div className="flex items-center justify-center h-40 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        )}
        {q.isError && <p className="text-sm text-destructive">Couldn't load this email.</p>}
        {v && v.unavailable && (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">{v.unavailable}</p>
            {v.preview && <p className="text-sm whitespace-pre-line border rounded-md p-3">{v.preview}</p>}
          </div>
        )}
        {srcDoc && (
          <iframe
            title="Email"
            sandbox="allow-popups allow-popups-to-escape-sandbox"
            srcDoc={srcDoc}
            className="w-full h-[60vh] border rounded-md bg-white"
          />
        )}
        {v?.body?.contentType === "text" && (
          <pre className="text-sm whitespace-pre-wrap font-sans max-h-[60vh] overflow-auto border rounded-md p-3">{v.body.content}</pre>
        )}
      </DialogContent>
    </Dialog>
  );
}
